/**
 * Medication routes (Phase 8, MED-01).
 * The same 14 codes as the backend `MED_ROUTES` enum (shared-utils/medRoutes.js). The labels are the
 * ones the medication form showed before codes existed, so the screen reads the same.
 */

export const MED_ROUTE_CODES = [
  'PO',
  'G_TUBE',
  'J_TUBE',
  'NG_TUBE',
  'NEBULIZED',
  'TRANSDERMAL',
  'TOPICAL',
  'OPHTHALMIC',
  'OTIC',
  'RECTAL',
  'IV',
  'IM',
  'SQ',
  'OTHER',
] as const;

export type MedRouteCode = typeof MED_ROUTE_CODES[number];

export const MED_ROUTE_LABELS: Record<MedRouteCode, string> = {
  PO: 'PO (Oral)',
  G_TUBE: 'G-tube (Gastrostomy)',
  J_TUBE: 'J-tube (Jejunostomy)',
  NG_TUBE: 'NG-tube (Nasogastric)',
  NEBULIZED: 'Nebulized',
  TRANSDERMAL: 'Transdermal Patch',
  TOPICAL: 'Topical (Skin)',
  OPHTHALMIC: 'Ophthalmic (Eye)',
  OTIC: 'Otic (Ear)',
  RECTAL: 'Rectal',
  IV: 'IV (Intravenous)',
  IM: 'IM (Intramuscular)',
  SQ: 'SQ (Subcutaneous)',
  OTHER: 'Other',
};

/** Options for a route `<select>`, in display order. */
export const MED_ROUTE_OPTIONS: { value: MedRouteCode; label: string }[] = MED_ROUTE_CODES.map((code) => ({
  value: code,
  label: MED_ROUTE_LABELS[code],
}));

export const isMedRouteCode = (value: unknown): value is MedRouteCode =>
  typeof value === 'string' && (MED_ROUTE_CODES as readonly string[]).includes(value);

/**
 * Map a stored route (a code, or legacy free text such as "PO (Oral)") to a code.
 * Unknown text maps to OTHER; the caller keeps the text as `routeOther`.
 */
export function toMedRouteCode(value: string | null | undefined): MedRouteCode | '' {
  if (!value) return '';
  const trimmed = value.trim();
  if (isMedRouteCode(trimmed)) return trimmed;
  const byLabel = MED_ROUTE_CODES.find((code) => MED_ROUTE_LABELS[code].toLowerCase() === trimmed.toLowerCase());
  return byLabel ?? 'OTHER';
}

/** Display text for a route: the label for a code, "Other: …" when routeOther is set, else the raw text. */
export function formatMedRoute(route: string | null | undefined, routeOther?: string | null): string {
  if (!route) return '';
  if (isMedRouteCode(route)) {
    if (route === 'OTHER' && routeOther) return `Other: ${routeOther}`;
    return MED_ROUTE_LABELS[route];
  }
  return route;
}
